import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail } from "lucide-react";
import { RevealLines, Reveal } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Brand Strategy",
  "Website Development",
  "Social Media",
  "Digital Advertising",
  "SEO / AEO",
  "PR & Communications",
];
const BUDGETS = ["< ₹50k", "₹50k – ₹2L", "₹2L – ₹5L", "₹5L+"];

const empty = { name: "", email: "", phone: "", service: "", budget: "", message: "" };

export const Enquiry = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#E8E4D9] py-3 text-base outline-none focus:border-[#C5A059] transition-colors placeholder:text-[#78716C]/60";

  return (
    <section id="enquiry" className="relative py-28 md:py-40" data-testid="enquiry">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-8">
            — Let&rsquo;s talk
          </p>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight">
            <RevealLines lines={["Let's build a", <em key="l" className="italic text-[#C5A059]">legacy</em>, "together."]} />
          </h2>
          <p className="text-base md:text-lg text-[#1A1713]/70 mt-8 max-w-md leading-relaxed">
            Brands are not just built — they are believed. Tell us where you want
            to go, and we&rsquo;ll shape the personality to get you there.
          </p>

          <div className="mt-12 space-y-5">
            <a
              href="tel:+918796953955"
              data-testid="contact-phone"
              className="flex items-center gap-4 group w-fit"
            >
              <span className="w-11 h-11 border border-[#E8E4D9] flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white transition-colors">
                <Phone size={18} />
              </span>
              <span className="text-lg link-underline">+91 87969 53955</span>
            </a>
            <a
              href="mailto:aurelumebranding@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-4 group w-fit"
            >
              <span className="w-11 h-11 border border-[#E8E4D9] flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white transition-colors">
                <Mail size={18} />
              </span>
              <span className="text-lg link-underline">aurelumebranding@gmail.com</span>
            </a>
          </div>
        </div>

        <Reveal>
          <form onSubmit={submit} className="space-y-8" data-testid="enquiry-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <input data-testid="form-name" className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} />
              <input data-testid="form-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
            </div>
            <input data-testid="form-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            <div className="grid sm:grid-cols-2 gap-8">
              <select data-testid="form-service" className={inputCls} value={form.service} onChange={set("service")}>
                <option value="">Service of interest</option>
                {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="form-budget" className={inputCls} value={form.budget} onChange={set("budget")}>
                <option value="">Estimated budget</option>
                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <textarea data-testid="form-message" rows={3} className={inputCls + " resize-none"} placeholder="Tell us about your brand *" value={form.message} onChange={set("message")} />
            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit"
              className="group inline-flex items-center gap-3 bg-[#1A1713] text-[#FDFBF7] px-9 py-5 text-xs uppercase tracking-[0.2em] disabled:opacity-50 transition-transform active:scale-95"
            >
              {loading ? "Sending" : "Send Enquiry"}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
